import LoadingButton from "@mui/lab/LoadingButton";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Paper from "@mui/material/Paper";
import Typography from "@mui/material/Typography";
import { useMutation } from "@tanstack/react-query";
import { getToken, isSupported, onMessage } from "firebase/messaging";
import { useEffect } from "react";
import { useBoolean } from "usehooks-ts";
import { messaging } from "~/libs/firebase";
import { gqlReq } from "~/libs/gql";
import { UpdateCurrentDeviceDocument } from "~/types/__generated/gql/graphql";
import { PresenceSnackbar } from "~served/ui-comps/components/overlay-presence/presence-overlays";

void (
	/* GraphQL */ `
		mutation UpdateCurrentDevice($input: UpdateCurrentDeviceInput!) {
			updateCurrentDevice(input: $input) {
				_id
			}
		}
	`
);

const useUpdateCurrentDeviceToken = () => {
	return useMutation({
		mutationFn: async () => {
			const token = await getToken(messaging, {
				vapidKey: process.env.FB_VAPID_KEY,
			});
			const variables = {
				input: { fcm_token: token },
			};
			return gqlReq({
				document: UpdateCurrentDeviceDocument,
				variables,
			});
		},
	});
};

export const PushNotification = () => {
	const { value: initMessaging, setValue: setInitMessaging } =
		useBoolean(false);
	const {
		value: showRequest,
		toggle: toggleShowRequest,
		setValue: setShowRequest,
	} = useBoolean(false);
	const updateCurrentDeviceToken = useUpdateCurrentDeviceToken();

	useEffect(() => {
		async function start() {
			const isBrowserSupported = await isSupported().catch(() => false);
			if (!isBrowserSupported) return;

			setShowRequest(Notification.permission === "default");

			if (Notification.permission === "granted") {
				await updateCurrentDeviceToken.mutateAsync();
				setInitMessaging(true);
			}
		}

		void start();

		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	useEffect(() => {
		if (initMessaging) {
			onMessage(messaging, (_payload) => undefined);
		}
	}, [initMessaging]);

	const handleRequestPermission = async () => {
		await updateCurrentDeviceToken.mutateAsync();
		setInitMessaging(true);
		toggleShowRequest();
	};

	if (showRequest)
		return (
			<PresenceSnackbar
				open
				autoHideDuration={3000}
				anchorOrigin={{ vertical: "bottom", horizontal: "center" }}>
				<Paper
					variant="elevation"
					sx={{ p: 2, display: "flex", flexDirection: "column", gap: 1 }}>
					<Typography variant="body2">
						Get notified about your order status in realtime
					</Typography>
					<Box
						display="flex"
						justifyContent="flex-end"
						gap={1}>
						<Button
							variant="outlined"
							color="inherit"
							disabled={updateCurrentDeviceToken.isLoading}
							onClick={toggleShowRequest}>
							Dismiss
						</Button>
						<LoadingButton
							loading={updateCurrentDeviceToken.isLoading}
							onClick={handleRequestPermission}>
							Allow notification
						</LoadingButton>
					</Box>
				</Paper>
			</PresenceSnackbar>
		);

	return null;
};
